/**
 * Environment configuration (TMMAPP-0001). This is a class-(b) runtime-bootstrap app
 * (react-normalization.md §1, MM-D-28): the API is reachable same-origin at `/api/*` and every
 * other platform host is fetched at runtime from `/api/bootstrap` (TMMAPP-0002). There are no URL
 * environment variables and no URL defaults. The only build variable is the deploy-environment
 * flag, and `vite.config.ts` refuses a build without it.
 *
 * `window.location` is the single source for the app origin. Missing browser location data is a
 * hard error because no safe fallback can be derived.
 */
export type DeployEnv = 'staging' | 'production';

export interface AppEnv {
  /** Deployment environment for visible labels and deploy-specific behavior. */
  deployEnv: DeployEnv;
  /** This app's own origin, derived from the browser at runtime. */
  appOrigin: string;
  /**
   * tmm-api base: this app's own origin plus the fixed `/api` prefix, rewritten at the edge onto
   * `/tmm-api` (design §8.1, MM-D-28). A product constant — never a build variable.
   */
  apiBaseUrl: string;
}

function requireBrowserLocation(): { origin: string; hostname: string } {
  const loc = typeof window !== 'undefined' ? window.location : undefined;
  if (!loc?.origin || !loc.hostname) {
    throw new Error('Cannot derive the app origin: window.location is unavailable.');
  }
  return { origin: loc.origin, hostname: loc.hostname };
}

function normalizeDeployEnv(value: string | undefined): DeployEnv | null {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'staging' || normalized === 'production') return normalized;
  return null;
}

/**
 * Fallback for a build that somehow shipped without the flag (the dev server, a test): a host
 * carrying a `staging` label is staging, anything else is production. Fail-safe in the direction
 * that never shows a staging badge on a production host.
 */
function inferDeployEnv(hostname: string): DeployEnv {
  return hostname.toLowerCase().split('.').includes('staging') ? 'staging' : 'production';
}

const API_PREFIX = '/api';

const browserLocation = requireBrowserLocation();

const deployEnv =
  normalizeDeployEnv(import.meta.env.VITE_DEPLOY_ENV) ?? inferDeployEnv(browserLocation.hostname);

export const env: AppEnv = {
  deployEnv,
  appOrigin: browserLocation.origin,
  apiBaseUrl: `${browserLocation.origin}${API_PREFIX}`,
};
